///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FoldersService } from '../../../services/project/folders.service';
import { finalize } from 'rxjs';
import { ProjectFolderDTO } from '../../../generated/shared-types.model';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { GtthEventManager } from '../../../services/event-manager.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-delete-folder-dialog',
    templateUrl: './delete-folder-dialog.component.html',
    standalone: false
})
export class GtthProjectsFolderDeleteDialogComponent implements OnInit {
    public folder: ProjectFolderDTO;
    public readonly loaderKey: LoaderIds = LoaderIds.DELETE_FOLDER_ID;

    constructor(
        public readonly preloaderService: PreloaderService,
        public dialogRef: MatDialogRef<GtthProjectsFolderDeleteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { folder: ProjectFolderDTO },
        private readonly projectFolderService: FoldersService,
        private readonly eventManager: GtthEventManager
    ) {}

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.confirmDelete();
        }
    }

    ngOnInit() {
        this.folder = this.data.folder;
    }

    confirmDelete() {
        this.preloaderService.show(this.loaderKey);

        this.projectFolderService
            .deleteFolder(this.folder.id)
            .pipe(
                finalize(() => {
                    this.preloaderService.hide(this.loaderKey);
                })
            )
            .subscribe((response) => {
                if (response === null) {
                    this.eventManager.broadcast({ name: 'folderModified' });
                    this.dialogRef.close();
                }
            });
    }
}

@Component({
    selector: 'winde-delete-folder-popup',
    template: '',
    standalone: false
})
export class GtthProjectsFolderDeletePopupComponent implements OnInit {
    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dialog: MatDialog,
        private readonly projectFolderService: FoldersService
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const id = params.has('id') ? parseInt(params.get('id'), 10) : undefined;

        if (isDefined(id)) {
            this.projectFolderService.getFolder(id).subscribe((folder: ProjectFolderDTO) => {
                if (!folder) {
                    this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                } else {
                    const dialogRef = this.dialog.open(GtthProjectsFolderDeleteDialogComponent, {
                        width: '800px',
                        position: {
                            top: '30px'
                        },
                        data: {
                            folder
                        },
                        closeOnNavigation: true
                    });

                    dialogRef.afterClosed().subscribe(() => {
                        this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                    });
                }
            });
        } else {
            this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
        }
    }
}
